import {
  AppError,
  EventEnvelopeSchema,
  type AppErrorShape,
  type DemoData,
  type EventAggregate,
  type EventEnvelope,
  type EventType,
  type Meta,
} from '@pointfive/contracts';
import { buildDemoData, DEMO_AS_OF } from './dataset';

/** Shared runtime of the Phase-1 sim: the seeded dataset, a deterministic clock and id counter, the outbox (ADR-003 —
 *  every state change records its event here in the same "transaction"), and the failure-injection hooks the /dev/console
 *  drives. Time is the reference present advanced by a counter and ids are counters — no wall clock, no randomness. */

export interface InjectOptions {
  /** The next broker dispatch is rejected (typed CONFLICT with the broker reason). */
  rejectNext?: boolean;
  /** The next broker dispatch times out after sending: the intent finalises `unconfirmed` (typed UNCONFIRMED). */
  unconfirmedNext?: boolean;
  /** The evaluator's store is unreachable: every order is refused fail-closed (LOCKED, source evaluator). */
  evaluatorDown?: boolean;
  /** Broker sync degraded: reads carry UPSTREAM_DEGRADED-worthy health; orders refuse. */
  brokerDegraded?: boolean;
  /** Rate-limit the next refresh-class mutation. */
  rateLimitNext?: boolean;
}
export interface InjectState {
  rejectNext: boolean;
  unconfirmedNext: boolean;
  evaluatorDown: boolean;
  brokerDegraded: boolean;
  rateLimitNext: boolean;
}

export interface SimClock {
  /** Current simulated instant (ISO). Reads never advance it. */
  now(): string;
  /** Advance by `seconds` (default 1) and return the new instant — every write ticks once. */
  tick(seconds?: number): string;
  /** ISO `n` seconds after now, without advancing. */
  plus(seconds: number): string;
  /** Today's ISO date (YYYY-MM-DD) at the simulated instant. */
  today(): string;
  /** Offset days from the reference present as ISO date. */
  dayAt(offsetDays: number): string;
}

export interface SimRuntime {
  readonly seed: string;
  readonly userId: string;
  readonly deviceId: string;
  readonly data: DemoData;
  readonly clock: SimClock;
  readonly inject: InjectState;
  readonly outbox: EventEnvelope[];
  /** Deterministic ids: `prefix_000001`. */
  id(prefix: string): string;
  /** Provenance marker for every response. */
  meta(): Meta;
  /** Records an event in the outbox (ADR-003); returns the envelope. */
  emit(
    type: EventType,
    aggregate: EventAggregate,
    aggregateId: string,
    payload: Record<string, unknown>,
    scope?: { accountId?: string | null; sessionId?: string | null },
  ): EventEnvelope;
  /** Deterministic 0..1 value from a string (FNV-1a hash; no randomness). */
  unit(key: string): number;
  /** Consume-once flags for the injection hooks. */
  takeInject(flag: keyof InjectState): boolean;
  fail(shape: AppErrorShape): never;
}

const BASE_MS = new Date(DEMO_AS_OF).getTime();
const isoAt = (ms: number) => new Date(ms).toISOString();

export function createSimRuntime(seed = 'pointfive-demo-2026'): SimRuntime {
  const data = buildDemoData(seed);
  let offsetS = 0;
  const counters = new Map<string, number>();
  const inject: InjectState = {
    rejectNext: false,
    unconfirmedNext: false,
    evaluatorDown: false,
    brokerDegraded: false,
    rateLimitNext: false,
  };
  const outbox: EventEnvelope[] = [];
  const clock: SimClock = {
    now: () => isoAt(BASE_MS + offsetS * 1000),
    tick: (s = 1) => {
      offsetS += s;
      return isoAt(BASE_MS + offsetS * 1000);
    },
    plus: (s) => isoAt(BASE_MS + (offsetS + s) * 1000),
    today: () => isoAt(BASE_MS + offsetS * 1000).slice(0, 10),
    dayAt: (off) => isoAt(BASE_MS - off * 86_400_000).slice(0, 10),
  };
  const id = (prefix: string) => {
    const n = (counters.get(prefix) ?? 0) + 1;
    counters.set(prefix, n);
    return `${prefix}_${String(n).padStart(6, '0')}`;
  };
  const rt: SimRuntime = {
    seed,
    userId: 'usr_demo',
    deviceId: 'dev_demo',
    data,
    clock,
    inject,
    outbox,
    id,
    meta: () => ({ mode: 'simulated', asOf: clock.now() }),
    emit: (type, aggregate, aggregateId, payload, scope = {}) => {
      const env = EventEnvelopeSchema.parse({
        id: id('evt'),
        type,
        version: 1,
        occurredAt: clock.now(),
        userId: rt.userId,
        accountId: scope.accountId ?? null,
        sessionId: scope.sessionId ?? null,
        aggregate,
        aggregateId,
        payload,
      });
      outbox.push(env);
      return env;
    },
    unit: (key) => {
      // FNV-1a over seed+key → [0,1). Deterministic by construction.
      let h = 0x811c9dc5;
      const s = `${seed}|${key}`;
      for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
      }
      return (h >>> 8) / 0x1000000;
    },
    takeInject: (flag) => {
      const v = inject[flag];
      if (flag === 'rejectNext' || flag === 'unconfirmedNext' || flag === 'rateLimitNext')
        inject[flag] = false;
      return v;
    },
    fail: (shape) => {
      throw new AppError(shape);
    },
  };
  return rt;
}

/** Cursor pagination helper: cursor = start index; stable order is the array order (id tiebreak by construction). */
export function paginate<T>(
  items: readonly T[],
  input: { cursor?: string | null; limit?: number } | undefined,
  meta: Meta,
) {
  const start = input?.cursor ? Math.max(0, Number.parseInt(input.cursor, 10) || 0) : 0;
  const limit = Math.min(200, Math.max(1, input?.limit ?? 50));
  const slice = items.slice(start, start + limit);
  const next = start + limit < items.length ? String(start + limit) : null;
  return { meta, items: slice, nextCursor: next };
}

/** Parse a reference display price ('4,028.40' → 4028.4). */
export function parsePx(display: string): number {
  const n = Number.parseFloat(display.replace(/[,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
}
